var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMemo } from 'react';
import { useFragment } from '@apollo/client/react';
import { t } from '@lingui/core/macro';
import { useToast } from '@pocketlaw/tetris';
import { useAccount } from 'shared/domains/users';
import { EmptyGrid } from './EmptyGrid';
import { EmptyGridReadonly } from './EmptyGridReadonly';
import { WidgetGridFragmentDoc } from './WidgetGrid.gql';
import { WidgetGridLayout } from './WidgetGridLayout';
import { useUpdateDashboardSlot } from './useUpdateDashboardSlot';
import { Widget } from '../Widget';
import { WidgetMenu } from '../Widget/WidgetMenu';
const isFullSlot = (slot) => slot != null &&
    typeof slot.id === 'string' &&
    typeof slot.widgetId === 'string' &&
    slot.widget != null;
export function WidgetGrid(props) {
    const { from } = props;
    const [updateSlots] = useUpdateDashboardSlot();
    const { addToast } = useToast();
    const account = useAccount();
    const { data, complete } = useFragment({
        from,
        fragment: WidgetGridFragmentDoc,
        fragmentName: 'WidgetGrid',
    });
    const onLayoutChange = (_, __, newItem) => __awaiter(this, void 0, void 0, function* () {
        const { i, h, w, x, y } = newItem;
        try {
            yield updateSlots({
                slotId: i,
                input: {
                    x,
                    y,
                    w,
                    h,
                },
            });
        }
        catch (_a) {
            addToast({
                title: t({
                    message: 'Widget update failed',
                    comment: 'Insights: Toast message when widget update failed',
                }),
                appearance: 'danger',
            });
        }
    });
    const safeSlots = useMemo(() => { var _a, _b; return (_b = (_a = data.slots) === null || _a === void 0 ? void 0 : _a.filter(isFullSlot)) !== null && _b !== void 0 ? _b : []; }, [data.slots]);
    const layouts = useMemo(() => ({
        lg: safeSlots === null || safeSlots === void 0 ? void 0 : safeSlots.map((slot) => ({
            i: slot.id,
            x: slot.x,
            y: slot.y,
            w: slot.w,
            h: slot.h,
            widgetId: slot.widgetId,
        })),
    }), [safeSlots]);
    if (!complete) {
        return null;
    }
    const { id, slots, filters, userId } = data;
    const isCreator = account.data.userId === userId;
    const emptyGrid = !(slots === null || slots === void 0 ? void 0 : slots.length);
    if (isCreator && emptyGrid) {
        return <EmptyGrid dashboardId={id}/>;
    }
    if (emptyGrid) {
        return <EmptyGridReadonly />;
    }
    return (<WidgetGridLayout onLayoutChange={onLayoutChange} editMode={isCreator} layouts={layouts}>
      {safeSlots === null || safeSlots === void 0 ? void 0 : safeSlots.map((slot) => (<div key={slot.id}>
          <Widget from={slot.widget} filters={filters} editMode={isCreator} actions={isCreator && <WidgetMenu widgetId={slot.widget.id} slotId={slot.id} dashboardId={id}/>}/>
        </div>))}
    </WidgetGridLayout>);
}
